/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Button, Grid, TextField, FormControlLabel, Switch, Checkbox, Box, Typography } from '@material-ui/core';
import { Field, Form, FormSpy } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { CountryAutoCompleteItem, MatterCountrySelector } from './selectors/MatterCountrySelector';
import { MatterTypeSelector, MatterTypeTypeAutoCompleteItem } from './selectors/MatterTypeSelector';
import { MatterPropertyAutoCompleteItem, MatterPropertySelector } from './selectors/MatterPropertySelector';
import { MatterCategoryAutoCompleteItem, MatterCategorySelector } from './selectors/MatterCategorySelector';
import { MatterClientSelector, NameSummaryAutoCompleteItem } from './selectors/MatterClientSelector';
import { MatterStatusAutoCompleteItem, MatterStatusSelector } from './selectors/MatterStatusSelector';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker/DatePicker';
import _ from 'lodash';
import { useTenant } from '../hooks/useTenant';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';
import { PriorityCountryAutoCompleteItem, PriorityCountrySelector } from './selectors/PriorityCountrySelector';
import { StaffSelector, StaffSummaryAutoCompleteItem } from './selectors/StaffSelector';
import { CurrencyInput2 } from '../components/CurrencyInput';
import { LocalStorageEnum } from '../enums/LocalStorageEnum';
import { Rating } from '@material-ui/lab';
import { useApplicationSettings } from '../applicationSettings/ApplicationSettingsRepository';
import { useCurrencySymbol } from '../hooks/useCurrencySymbol';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flex: 1,
            '& .downshift-wrapper': {
                flex: 1,
                padding: 0,
            },
        },
        col1: {
            flex: 1,
            maxWidth: '75%',
        },
        col2: {
            padding: '0 10px',
        },
        textField: {
            width: '100%',
        },
        textFieldRight: {
            '& .MuiInputBase-input': {
                textAlign: 'right',
            }
        },
        dateField: {
            width: '100%',
        },
        button: {
            minWidth: '83px',
        },
        addButton: {
            textTransform: 'none',
        },
        clientSelector: {
            flex: 1,
        },
        removedAvatar: {
            maxWidth: '15px',
            maxHeight: '15px',
        },
        addProperyWrapperGrid: {
            display: 'flex',
            alignItems: 'flex-end',
            padding: '4px',
        },
        divider: {
            width: '100%',
        },
        switchLabel: {
            margin: 0,
        },
        textFieldWithToggleWrapper: {
            display: 'flex',
            alignItems: 'center',
        },
    }),
);

interface MatterSummaryAdvancedFilterProps {
    advancedFilter?: AdvancedFilterForm;
    isLoading?: boolean;
    onApplySearch: (filter: AdvancedFilterForm, hasFilter: boolean) => void;
    /** Reports whether this panel's own tenant/settings data is still loading */
    onFieldsLoading?: (isLoading: boolean) => void;
}

export interface MatterCountry {
    matterCountry: CountryAutoCompleteItem[];
    exclude: boolean;
}

export interface CaseType {
    caseType: MatterTypeTypeAutoCompleteItem[];
    exclude: boolean;
}

export interface MatterProperty {
    matterProperty: MatterPropertyAutoCompleteItem[];
    exclude: boolean;
}

export interface MatterCategory {
    matterCategory: MatterCategoryAutoCompleteItem[];
    exclude: boolean;
}

export interface MatterClient {
    matterClient: NameSummaryAutoCompleteItem[];
    exclude: boolean;
}

export interface MatterOwner {
    matterOwner: NameSummaryAutoCompleteItem[];
    exclude: boolean;
}

export interface MatterStatus {
    matterStatus: MatterStatusAutoCompleteItem[];
    exclude: boolean;
}

export interface PriorityCountry {
    priorityCountry: PriorityCountryAutoCompleteItem[];
    exclude: boolean;
}

export interface PersonActing {
    personActing: StaffSummaryAutoCompleteItem[];
    exclude: boolean;
}

export interface PersonResponsible {
    personResponsible: StaffSummaryAutoCompleteItem[];
    exclude: boolean;
}

export interface PersonIntroducing {
    personIntroducing: StaffSummaryAutoCompleteItem[];
    exclude: boolean;
}

export interface PersonAssisting {
    personAssisting: StaffSummaryAutoCompleteItem[];
    exclude: boolean;
}

export interface AdvancedFilterForm {
    ourReference?: string;
    yourReference?: string;
    matterCountries: MatterCountry;
    caseTypes: CaseType;
    matterProperties: MatterProperty;
    matterCategories: MatterCategory;
    matterClients: MatterClient;
    matterOwners: MatterOwner;
    matterStatuses: MatterStatus;
    filingDateFrom?: Date;
    filingDateTo?: Date;
    applicationNumber?: string;
    grantAndRegistrationDateFrom?: Date;
    grantAndRegistrationDateTo?: Date;
    registrationNumber?: string;
    priorityDateFrom?: Date;
    priorityDateTo?: Date;
    title?: string;
    class?: string;
    goodsAndServices?: string;
    priorityCountries: PriorityCountry;
    instructionDateFrom?: Date;
    instructionDateTo?: Date;
    completionDateFrom?: Date;
    completionDateTo?: Date;
    personActings: PersonActing;
    personResponsibles: PersonResponsible;
    personIntroducings: PersonIntroducing;
    personAssistings: PersonAssisting;
    isMatterEstimateZero: boolean;
    feesBilledVsEstimatePercentage: number;
    importanceLevel: number;
    totalDebtGreaterThanAmount?: number;
    unbilledFeesGreaterThanAmount?: number;
}

export const MatterSummaryAdvancedFilter: React.FC<MatterSummaryAdvancedFilterProps> = (props) => {
    const classes = useStyles();

    const tenant = useTenant();

    const currencyQuery = useCurrencySymbol();

    const applicationSettingsQuery = useApplicationSettings('cache-first');

    const isFieldsLoading = tenant.loading || applicationSettingsQuery.loading;

    useEffect(() => {
        if (props.onFieldsLoading) {
            props.onFieldsLoading(isFieldsLoading);
        }
    // tslint:disable-next-line: align
    }, [isFieldsLoading]);

    const defaultMatterCountry: MatterCountry = {
        matterCountry: [],
        exclude: false,
    };

    const defaultCaseType: CaseType = {
        caseType: [],
        exclude: false,
    };

    const defaultMatterProperty: MatterProperty = {
        matterProperty: [],
        exclude: false,
    };

    const defaultMatterCategory: MatterCategory = {
        matterCategory: [],
        exclude: false,
    };

    const defaultMatterClient: MatterClient = {
        matterClient: [],
        exclude: false,
    };

    const defaultMatterOwner: MatterOwner = {
        matterOwner: [],
        exclude: false,
    };

    const defaultMatterStatus: MatterStatus = {
        matterStatus: [],
        exclude: false,
    };

    const defaultPriorityCountry: PriorityCountry = {
        priorityCountry: [],
        exclude: false,
    };

    const defaultPersonActings: PersonActing = {
        personActing: [],
        exclude: false,
    };

    const defaultPersonResponsibles: PersonResponsible = {
        personResponsible: [],
        exclude: false,
    };

    const defaultPersonIntroducings: PersonIntroducing = {
        personIntroducing: [],
        exclude: false,
    };

    const defaultPersonAssistings: PersonAssisting = {
        personAssisting: [],
        exclude: false,
    };

    const defaultValues: AdvancedFilterForm = {
        ourReference: undefined,
        yourReference: undefined,
        matterCountries: defaultMatterCountry,
        caseTypes: defaultCaseType,
        matterProperties: defaultMatterProperty,
        matterCategories: defaultMatterCategory,
        matterClients: defaultMatterClient,
        matterOwners: defaultMatterOwner,
        matterStatuses: defaultMatterStatus,
        filingDateFrom: undefined,
        filingDateTo: undefined,
        applicationNumber: undefined,
        grantAndRegistrationDateFrom: undefined,
        grantAndRegistrationDateTo: undefined,
        registrationNumber: undefined,
        priorityDateFrom: undefined,
        priorityDateTo: undefined,
        title: undefined,
        class: undefined,
        goodsAndServices: undefined,
        priorityCountries: defaultPriorityCountry,
        completionDateFrom: undefined,
        completionDateTo: undefined,
        instructionDateFrom: undefined,
        instructionDateTo: undefined,
        personActings: defaultPersonActings,
        personResponsibles: defaultPersonResponsibles,
        personIntroducings: defaultPersonIntroducings,
        personAssistings: defaultPersonAssistings,
        feesBilledVsEstimatePercentage: 0,
        isMatterEstimateZero: false,
        importanceLevel: 0,
    };

    let initialValues: AdvancedFilterForm = {
        ...defaultValues,
    };

    if (props.advancedFilter) {
        initialValues = props.advancedFilter;
    }

    const onSubmit = (form: FormApi<AdvancedFilterForm>, values: AdvancedFilterForm) => {
        let hasFilter = false;

        if (!_.isMatch(defaultValues, values)) {
            hasFilter = true;
        }

        if (props.onApplySearch) {
            props.onApplySearch(values, hasFilter);
        }
    };

    const onReset = (form: FormApi<AdvancedFilterForm>) => () => {
        form.setConfig('keepDirtyOnReinitialize', false);
        form.batch(() => {
            form.change('ourReference', undefined);
            form.change('yourReference', undefined);
            form.change('matterCountries', defaultMatterCountry);
            form.change('caseTypes', defaultCaseType);
            form.change('matterProperties', defaultMatterProperty);
            form.change('matterCategories', defaultMatterCategory);
            form.change('matterClients', defaultMatterClient);
            form.change('matterOwners', defaultMatterOwner);
            form.change('matterStatuses', defaultMatterStatus);
            form.change('filingDateFrom', undefined);
            form.change('filingDateTo', undefined);
            form.change('applicationNumber', undefined);
            form.change('grantAndRegistrationDateFrom', undefined);
            form.change('grantAndRegistrationDateTo', undefined);
            form.change('registrationNumber', undefined);
            form.change('priorityDateFrom', undefined);
            form.change('priorityDateTo', undefined);
            form.change('title', undefined);
            form.change('class', undefined);
            form.change('goodsAndServices', undefined);
            form.change('priorityCountries', defaultPriorityCountry);
            form.change('completionDateFrom', undefined);
            form.change('completionDateTo', undefined);
            form.change('instructionDateFrom', undefined);
            form.change('instructionDateTo', undefined);
            form.change('isMatterEstimateZero', false);
            form.change('feesBilledVsEstimatePercentage', undefined);

            form.change('personActings', defaultPersonActings);
            form.change('personResponsibles', defaultPersonResponsibles);
            form.change('personIntroducings', defaultPersonIntroducings);
            form.change('personAssistings', defaultPersonAssistings);
            form.change('importanceLevel', 0);
            form.change('totalDebtGreaterThanAmount', undefined);
            form.change('unbilledFeesGreaterThanAmount', undefined);
        });
        form.setConfig('keepDirtyOnReinitialize', true);
    };

    const actionButtons = (
        form: FormApi<AdvancedFilterForm>,
        submitting: boolean,
        pristine: boolean,
    ) => {
        return (
            <div className={classes.col2}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        type="submit"
                        className={classes.button}
                        disabled={submitting || pristine}
                    >
                        Apply Search
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        type="button"
                        className={classes.button}
                        onClick={onReset(form)}
                    >
                        Clear
                    </Button>
                </div>
            </div>
        );
    };

    const ourReferenceField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name={`ourReference`} subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <TextField
                        {...input}
                        label="Attorney Reference"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        error={meta.error && meta.touched}
                        size="small"
                    />
                )}
            </Field>
        );
    };

    const yourReferenceField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name={`yourReference`} subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <TextField
                        {...input}
                        label="Client Reference"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        error={meta.error && meta.touched}
                        size="small"
                    />
                )}
            </Field>
        );
    };

    const matterCountryField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`matterCountries.matterCountry`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <MatterCountrySelector
                            {...input}
                            label="Country"
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: CountryAutoCompleteItem[] | CountryAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            multiple={true}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>
                <Field name={`matterCountries.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const caseTypeField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        const isInprotech = tenant.data!.tenant.sourceSystem.toLocaleLowerCase() === 'inprotech';

        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`caseTypes.caseType`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <MatterTypeSelector
                            {...input}
                            label={isInprotech ? 'Case Type' : 'Matter Type'}
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: MatterTypeTypeAutoCompleteItem[] | MatterTypeTypeAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            multiple={true}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>
                <Field name={`caseTypes.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const matterPropertyField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`matterProperties.matterProperty`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <MatterPropertySelector
                            {...input}
                            label="Property Type"
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: MatterPropertyAutoCompleteItem[] | MatterPropertyAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            multiple={true}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>
                <Field name={`matterProperties.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const matterCategoryField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        const isInprotech = tenant.data!.tenant.sourceSystem.toLocaleLowerCase() === 'inprotech';

        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`matterCategories.matterCategory`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <MatterCategorySelector
                            {...input}
                            label={isInprotech ? 'Case Category' : 'Matter Category'}
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: MatterCategoryAutoCompleteItem[] | MatterCategoryAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            multiple={true}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>
                <Field name={`matterCategories.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const matterClientField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`matterClients.matterClient`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <MatterClientSelector
                            {...input}
                            value={input.value}
                            label="Client"
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: NameSummaryAutoCompleteItem[] | NameSummaryAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            isClientsOnly={true}
                            includeIndividual={true}
                            includeOrganisation={true}
                            multiple={true}
                            isMatterOwnersOnly={false}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>
                <Field name={`matterClients.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const matterOwnerField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`matterOwners.matterOwner`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <MatterClientSelector
                            {...input}
                            value={input.value}
                            label="Owner"
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: NameSummaryAutoCompleteItem[] | NameSummaryAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            isClientsOnly={false}
                            isMatterOwnersOnly={true}
                            includeIndividual={true}
                            includeOrganisation={true}
                            multiple={true}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>

                <Field name={`matterOwners.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const matterStatusField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`matterStatuses.matterStatus`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <MatterStatusSelector
                            {...input}
                            label="Status"
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: MatterStatusAutoCompleteItem[] | MatterStatusAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            multiple={true}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>

                <Field name={`matterStatuses.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const matterFilingDateFieldFrom = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="filingDateFrom" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Filing Date From"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                maxDate={values.filingDateTo ? values.filingDateTo : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const matterFilingDateFieldTo = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="filingDateTo" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Filing Date To"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                minDate={values.filingDateFrom ? values.filingDateFrom : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const applicationNumberField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name="applicationNumber" subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <TextField
                        {...input}
                        label="Application Number"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        error={meta.error && meta.touched}
                        size="small"
                    />
                )}
            </Field>
        );
    };

    const grantAndRegistrationDateFieldFrom = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="grantAndRegistrationDateFrom" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Grant/Registration Date From"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                maxDate={values.grantAndRegistrationDateTo ? values.grantAndRegistrationDateTo : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const grantAndRegistrationDateFieldTo = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="grantAndRegistrationDateTo" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Grant/Registration Date To"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                minDate={values.grantAndRegistrationDateFrom ? values.grantAndRegistrationDateFrom : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const registrationNumberField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name="registrationNumber" subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <TextField
                        {...input}
                        label="Registration/Grant Number"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        error={meta.error && meta.touched}
                        size="small"
                    />
                )}
            </Field>
        );
    };

    const priorityDateFieldFrom = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="priorityDateFrom" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Priority Date From"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                maxDate={values.priorityDateTo ? values.priorityDateTo : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const priorityDateFieldTo = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="priorityDateTo" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Priority Date To"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                minDate={values.priorityDateFrom ? values.priorityDateFrom : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const priorityCountryField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <div className={classes.textFieldWithToggleWrapper}>
                <Field name={`priorityCountries.priorityCountry`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <PriorityCountrySelector
                            {...input}
                            label="Priority Country"
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            error={meta.error && meta.touched}
                            onSelection={(selection?: PriorityCountryAutoCompleteItem[] | PriorityCountryAutoCompleteItem) => {
                                input.onChange(selection);
                            }}
                            onBlur={input.onBlur}
                            multiple={true}
                            isApplyMatterAccess={true}
                        />
                    )}
                </Field>
                <Field name={`priorityCountries.exclude`} subscription={{ touched: true, error: true, value: true }}>
                    {({ input, meta }) => (
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                            label="Not"
                        />
                    )}
                </Field>
            </div>
        );
    };

    const titleField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name="title" subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <TextField
                        {...input}
                        label="Title/Mark"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        error={meta.error && meta.touched}
                        size="small"
                    />
                )}
            </Field>
        );
    };

    const classField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name="class" subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <TextField
                        {...input}
                        label="Class"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        error={meta.error && meta.touched}
                        size="small"
                    />
                )}
            </Field>
        );
    };

    const goodsAndServicesField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name="goodsAndServices" subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <TextField
                        {...input}
                        label="Goods And Services"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        error={meta.error && meta.touched}
                        size="small"
                    />
                )}
            </Field>
        );
    };

    const instructionDateFieldFrom = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="instructionDateFrom" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Instruction Date From"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                maxDate={values.instructionDateTo ? values.instructionDateTo : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const instructionDateFieldTo = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="instructionDateTo" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Instruction Date To"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                minDate={values.instructionDateFrom ? values.instructionDateFrom : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const completionDateFieldFrom = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="completionDateFrom" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Completion Date From"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                maxDate={values.completionDateTo ? values.completionDateTo : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const completionDateFieldTo = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <FormSpy subscription={{ values: true, validating: true }}>
                {/* tslint:disable-next-line: no-shadowed-variable */}
                {({ values }) => (
                    <Field name="completionDateTo" subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <KeyboardDatePicker
                                {...input}
                                value={input && input.value ? input.value : null}
                                className={classes.dateField}
                                label="Completion Date To"
                                format="DD/MM/YYYY"
                                placeholder="dd/mm/yyyy"
                                animateYearScrolling={true}
                                autoOk={true}
                                allowKeyboardControl={false}
                                variant={'inline'}
                                inputVariant="outlined"
                                error={meta.error && meta.touched}
                                inputProps={{ autocomplete: 'off' }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                minDate={values.completionDateFrom ? values.completionDateFrom : undefined}
                            />
                        )}
                    </Field>
                )}
            </FormSpy>
        );
    };

    const personActingField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {

        if (applicationSettingsQuery.data?.settings.applications.labelSettings.config.personActingLabel?.length === 0) {
            return null;
        }

        return (
            <Grid item={true} xs={12} md={3}>
                <div className={classes.textFieldWithToggleWrapper}>
                    <Field name={`personActings.personActing`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <StaffSelector
                                {...input}
                                value={input.value}
                                label={applicationSettingsQuery.data?.settings.applications.labelSettings.config.personActingLabel || localStorage.getItem(LocalStorageEnum.PersonActing) || 'Person Acting'}
                                variant="outlined"
                                size="small"
                                className={classes.textField}
                                error={meta.error && meta.touched}
                                onSelection={(selection?: StaffSummaryAutoCompleteItem[] | StaffSummaryAutoCompleteItem) => {
                                    input.onChange(selection);
                                }}
                                onBlur={input.onBlur}
                                multiple={true}
                            />
                        )}
                    </Field>

                    <Field name={`personActings.exclude`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <FormControlLabel
                                className={classes.switchLabel}
                                control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                                label="Not"
                            />
                        )}
                    </Field>
                </div>
            </Grid>
        );
    };

    const personResponsibleField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        if (applicationSettingsQuery.data?.settings.applications.labelSettings.config.personResponsibleLabel?.length === 0) {
            return null;
        }
        return (
            <Grid item={true} xs={12} md={3}>
                <div className={classes.textFieldWithToggleWrapper}>
                    <Field name={`personResponsibles.personResponsible`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <StaffSelector
                                {...input}
                                value={input.value}
                                label={applicationSettingsQuery.data?.settings.applications.labelSettings.config.personResponsibleLabel || localStorage.getItem(LocalStorageEnum.PersonResponsible) || 'Person Responsible'}
                                variant="outlined"
                                size="small"
                                className={classes.textField}
                                error={meta.error && meta.touched}
                                onSelection={(selection?: StaffSummaryAutoCompleteItem[] | StaffSummaryAutoCompleteItem) => {
                                    input.onChange(selection);
                                }}
                                onBlur={input.onBlur}
                                multiple={true}
                            />
                        )}
                    </Field>

                    <Field name={`personResponsibles.exclude`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <FormControlLabel
                                className={classes.switchLabel}
                                control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                                label="Not"
                            />
                        )}
                    </Field>
                </div>
            </Grid>
        );
    };

    const personAssistingField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        if (applicationSettingsQuery.data?.settings.applications.labelSettings.config.matterAssistingResponsibilityLabel?.length === 0) {
            return null;
        }
        return (
            <Grid item={true} xs={12} md={3}>
                <div className={classes.textFieldWithToggleWrapper}>
                    <Field name={`personAssistings.personAssisting`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <StaffSelector
                                {...input}
                                value={input.value}
                                label={applicationSettingsQuery.data?.settings.applications.labelSettings.config.matterAssistingResponsibilityLabel || localStorage.getItem(LocalStorageEnum.MatterAssistingResponsibility) || 'Person Assisting'}
                                variant="outlined"
                                size="small"
                                className={classes.textField}
                                error={meta.error && meta.touched}
                                onSelection={(selection?: StaffSummaryAutoCompleteItem[] | StaffSummaryAutoCompleteItem) => {
                                    input.onChange(selection);
                                }}
                                onBlur={input.onBlur}
                                multiple={true}
                            />
                        )}
                    </Field>

                    <Field name={`personAssistings.exclude`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <FormControlLabel
                                className={classes.switchLabel}
                                control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                                label="Not"
                            />
                        )}
                    </Field>
                </div>
            </Grid>
        );
    };

    const personIntroducingField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {

        if (applicationSettingsQuery.data?.settings.applications.labelSettings.config.matterIntroducingResponsibilityLabel?.length === 0) {
            return null;
        }

        return (
            <Grid item={true} xs={12} md={3}>
                <div className={classes.textFieldWithToggleWrapper}>
                    <Field name={`personIntroducings.personIntroducing`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <StaffSelector
                                {...input}
                                value={input.value}
                                label={applicationSettingsQuery.data?.settings.applications.labelSettings.config.matterIntroducingResponsibilityLabel || localStorage.getItem(LocalStorageEnum.MatterIntroducingResponsibility) || 'Credit'}
                                variant="outlined"
                                size="small"
                                className={classes.textField}
                                error={meta.error && meta.touched}
                                onSelection={(selection?: StaffSummaryAutoCompleteItem[] | StaffSummaryAutoCompleteItem) => {
                                    input.onChange(selection);
                                }}
                                onBlur={input.onBlur}
                                multiple={true}
                            />
                        )}
                    </Field>

                    <Field name={`personIntroducings.exclude`} subscription={{ touched: true, error: true, value: true }}>
                        {({ input, meta }) => (
                            <FormControlLabel
                                className={classes.switchLabel}
                                control={<Switch {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                                label="Not"
                            />
                        )}
                    </Field>
                </div>
            </Grid>
        );
    };

    const matterEstimateCheckbox = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name={`isMatterEstimateZero`} subscription={{ touched: true, error: true, value: true }}>
                {({ input, meta }) => (
                    <FormControlLabel
                        className={classes.switchLabel}
                        control={<Checkbox {...input} checked={input.value} onChange={input.onChange} color="primary" size="small" />}
                        label="Matter estimate is zero"
                    />
                )}
            </Field>
        );
    };

    const feesBilledVsEstimateField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field
                name="feesBilledVsEstimatePercentage"
                label={'Fees Billed vs Estimated greater than'}
                suffix="%"
                className={`${classes.textField} ${classes.textFieldRight}`}
                component={CurrencyInput2}
                size="small"
                variant="outlined"
            />
        );
    };

    const unbilledFeesGreaterThanAmountField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field
                name="unbilledFeesGreaterThanAmount"
                label={'Unbilled Fees greater than'}
                prefix={currencyQuery.data?.options.currencySymbol.symbol || '$'}
                className={`${classes.textField} ${classes.textFieldRight}`}
                component={CurrencyInput2}
                size="small"
                variant="outlined"
            />
        );
    };

    const totalDebtGreaterThanAmount = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field
                name="totalDebtGreaterThanAmount"
                label={'Total Debt greater than'}
                prefix={currencyQuery.data?.options.currencySymbol.symbol || '$'}
                className={`${classes.textField} ${classes.textFieldRight}`}
                component={CurrencyInput2}
                size="small"
                variant="outlined"
            />
        )
    };

    const importanceLevelField = (form: FormApi<AdvancedFilterForm>, submitting: boolean, pristine: boolean, values: AdvancedFilterForm) => {
        return (
            <Field name={`importanceLevel`} subscription={{ touched: true, error: true, value: true }}>
                {({ input }) => (
                    <Box
                        component="div"
                        display="flex"
                        flexDirection="column"
                    >
                        <Typography
                            component="label"
                            style={{
                                color: 'rgba(0, 0, 0, 0.54)',
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                lineHeight: 1,
                                letterSpacing: '0.00938em',
                            }}
                        >
                            Importance Level
                        </Typography>
                        <Rating
                            {...input}
                            onChange={(event, newValue) => {
                                input.onChange(newValue ?? 0);
                            }}
                            max={5}
                            size="medium"
                            style={{ marginTop: '4px' }}
                        />
                    </Box>
                )}
            </Field>
        );
    };

    if (isFieldsLoading) {
        return (
            <div className={classes.root}>
                <SkeletonLoader variant="form" rows={4} columns={4} />
            </div>
        );
    }

    const isInprotech = tenant.data!.tenant.sourceSystem.toLocaleLowerCase() === 'inprotech';

    return (
        <Form
            onSubmit={(values, form: FormApi<AdvancedFilterForm>) => onSubmit(form, values)}
            mutators={{ ...arrayMutators }}
            initialValues={initialValues}
            subscription={{ submitting: true, pristine: true }}
            keepDirtyOnReinitialize={true}
            render={({ handleSubmit, form, submitting, pristine, values }) => (
                <form onSubmit={handleSubmit} className={classes.root}>
                    <div className={classes.col1}>
                        {isInprotech ? (
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={4}>
                                            {ourReferenceField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {yourReferenceField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterCountryField(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={4}>
                                            {caseTypeField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterPropertyField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterCategoryField(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterClientField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterOwnerField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterStatusField(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={4}>
                                            {applicationNumberField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterFilingDateFieldFrom(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {matterFilingDateFieldTo(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={4}>
                                            {registrationNumberField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {grantAndRegistrationDateFieldFrom(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {grantAndRegistrationDateFieldTo(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={4}>
                                            {priorityCountryField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {priorityDateFieldFrom(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {priorityDateFieldTo(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={4}>
                                            {titleField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {classField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={4}>
                                            {goodsAndServicesField(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={3}>
                                            {matterClientField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {matterStatusField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {caseTypeField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {matterCategoryField(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        {personActingField(form, submitting, pristine, values)}
                                        {personResponsibleField(form, submitting, pristine, values)}
                                        {personAssistingField(form, submitting, pristine, values)}
                                        {personIntroducingField(form, submitting, pristine, values)}
                                    </Grid>
                                </Grid>

                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={3}>
                                            {instructionDateFieldFrom(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {instructionDateFieldTo(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {completionDateFieldFrom(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {completionDateFieldTo(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={3}>
                                            {unbilledFeesGreaterThanAmountField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {totalDebtGreaterThanAmount(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {feesBilledVsEstimateField(form, submitting, pristine, values)}
                                        </Grid>
                                        <Grid item={true} xs={12} md={3}>
                                            {matterEstimateCheckbox(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item={true} xs={12} md={12}>
                                    <Grid container={true} spacing={2}>
                                        <Grid item={true} xs={12} md={3}>
                                            {importanceLevelField(form, submitting, pristine, values)}
                                        </Grid>
                                    </Grid>
                                </Grid>

                            </Grid>
                        )}
                    </div>
                    {actionButtons(form, submitting, pristine)}
                </form>
            )}
        />
    );
};
